// frontend/src/components/homepage/LeadCardLive.js
//
// The hero's lead card: the one piece of the page that shows, rather than
// describes, what the product does.
//
// PRESENTATIONAL ONLY. It owns no timer, no animation and no cycle - it renders
// whatever lead, score and heat state it is handed. The cycle that drives it
// lives in useHeroCycle.js, and the two refs this component forwards are the
// only handles that cycle needs:
//
//   * `frontRef`  the positioned wrapper. The cycle toggles `hl-lead-out` on it
//                 to file a finished card down and away.
//   * `cardRef`   the card surface itself. The cycle animates THIS with the Web
//                 Animations API when the card grows out of an ignited node, and
//                 writes its opacity directly. Two elements, because the file-out
//                 is a CSS transition on the wrapper and the entrance is a WAAPI
//                 transform on the card - running both on one element would have
//                 them fight over `transform`.
//
// SPLIT OUT OF HeroLive.js in P.4: the hero file was carrying the copy column,
// the stage, the tray and 200 lines of card markup, and the card is the part
// that now changes eight times a minute.

import React, { forwardRef } from "react";
import { Box } from "@mui/material";

const iconSx = {
  width: 12,
  height: 12,
  stroke: "var(--hl-ink-3)",
  fill: "none",
  strokeWidth: 1.8,
};

const ClockIcon = () => (
  <Box component="svg" viewBox="0 0 24 24" aria-hidden="true" sx={iconSx}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Box>
);

const PinIcon = () => (
  <Box component="svg" viewBox="0 0 24 24" aria-hidden="true" sx={iconSx}>
    <path d="M12 22s7-7 7-12a7 7 0 0 0-14 0c0 5 7 12 7 12z" />
    <circle cx="12" cy="10" r="2.5" />
  </Box>
);

/**
 * @param {object} props
 * @param {object} props.lead a row from heroData's LEADS.
 * @param {number} props.score the score to render on MOUNT. The cycle does not
 *   re-render for the count: it writes the figure and the bar through
 *   `scoreRef` / `barRef` per frame (HeroLive.writeScore), so this prop is the
 *   still page's finished value, or 0 when the cycle is about to run.
 * @param {React.RefObject} [props.scoreRef] the `<b>` carrying the figure.
 * @param {React.RefObject} [props.barRef] the bar's fill element.
 * @param {boolean} props.hot render the coral rim and the HOT LEAD stamp.
 * @param {string} props.scoreLabel
 * @param {string} props.hotStamp
 * @param {object} [props.sx] extra styles for the positioned wrapper.
 */
const LeadCardLive = forwardRef(function LeadCardLive(
  { lead, score, hot, scoreLabel, hotStamp, cardRef, scoreRef, barRef, sx },
  frontRef
) {
  const accent = hot ? "var(--hl-ember-2)" : "var(--hl-teal)";

  return (
    <Box ref={frontRef} className="hl-lead" sx={sx}>
      <Box
        ref={cardRef}
        className="hl-lead-card"
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: "262px",
          px: "24px",
          py: "22px",
          borderRadius: "var(--hl-radius-card)",
          background: "rgba(255,255,255,.94)",
          border: `1px solid ${hot ? "rgba(191,88,103,.55)" : "rgba(64,224,208,.35)"}`,
          boxShadow: hot
            ? "0 20px 40px rgba(191,88,103,.2), 0 16px 20px rgba(0,0,0,.08), inset 0 0 0 1px rgba(191,88,103,.2)"
            : "0 20px 40px rgba(64,224,208,.22), 0 16px 20px rgba(0,0,0,.08), inset 0 1px 0 rgba(255,255,255,.95)",
          transition: "border-color .8s, box-shadow .8s",
        }}
      >
        {hot && (
          <Box
            component="span"
            className="hl-stamp"
            sx={{
              position: "absolute",
              top: "16px",
              insetInlineEnd: "16px",
              fontFamily: "var(--hl-mono)",
              fontSize: "10px",
              letterSpacing: ".2em",
              color: "var(--hl-ember)",
              border: "1px solid rgba(191,88,103,.5)",
              px: "8px",
              py: "3px",
              borderRadius: "6px",
              transform: "rotate(-8deg)",
            }}
          >
            {hotStamp}
          </Box>
        )}

        {/* `hl-lead-head`, `hl-lead-chip`, `hl-lead-group`, `hl-lead-meta`,
            `hl-lead-text`, `hl-lead-score` and `hl-lead-bar` are LAYOUT HOOKS
            for the phone block in homepage.css, and they carry no rule of their
            own outside `@media (max-width: 900px) .hl-hero-slide ...` - so the
            desktop card's DOM, boxes and computed styles are untouched by their
            presence, and the phone rules cannot reach it even at a narrow
            desktop window (the slide exists only inside the phone wheel).

            They exist because the phone card is a quarter shorter than this one
            and wears one chrome whether it is hot or not (owner round 5): every
            row's padding, the text's two-line clamp and the teal rim are set
            from there. The header pair came first, in round 4: the HOT stamp is
            absolutely placed in this card's top end corner, which on a
            phone-width card lands squarely on the group line, so the phone rule
            wraps the header and the group line takes a full line of its own
            underneath while the stamp sits in the space left beside the chip.

            Structural selectors (`> div:nth-of-type(3)`) would have done the
            same job without touching this file, and would have broken the first
            time anyone added an element here. */}
        <Box
          className="hl-lead-head"
          sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "10px" }}
        >
          <Box
            component="span"
            // `hl-on` from the first render, like the prototype's chip, whose
            // `on` is set by the first fill before the first paint. The
            // scale/opacity entrance is armed in homepage.css, not shown.
            className="hl-lead-chip hl-on"
            sx={{
              flex: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--hl-teal)",
              border: "1px solid rgba(143,212,209,.35)",
              background: "rgba(107,170,168,.1)",
              px: "10px",
              py: "4px",
              borderRadius: "8px",
            }}
          >
            {lead.pro}
          </Box>
          <Box
            component="span"
            className="hl-lead-group"
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: "var(--hl-mono)",
              fontSize: "11px",
              color: "var(--hl-ink-2)",
              letterSpacing: ".03em",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {lead.grp}
          </Box>
        </Box>

        <Box
          className="hl-lead-meta"
          sx={{
            display: "flex",
            gap: "14px",
            alignItems: "center",
            minHeight: "22px",
            fontFamily: "var(--hl-mono)",
            fontSize: "12px",
            color: "var(--hl-ink-3)",
          }}
        >
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
            <ClockIcon />
            <span>{lead.time}</span>
          </Box>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
            <PinIcon />
            <span>{lead.city}</span>
          </Box>
        </Box>

        <Box
          className="hl-lead-text"
          sx={{
            mt: "14px",
            px: "14px",
            py: "12px",
            minHeight: "78px",
            fontSize: "14.5px",
            lineHeight: 1.7,
            color: "var(--hl-ink-2)",
            border: "1px solid var(--hl-hair)",
            borderRadius: "var(--hl-radius-inner)",
            background: "rgba(0,90,82,.03)",
          }}
        >
          {/* An unstyled inline SPAN on a desktop - it inherits everything and
              changes no box. It exists for the phone card's two-line clamp, and
              it has to be a separate element: `-webkit-line-clamp` on the padded
              box above sets the box to two lines but `overflow: hidden` clips at
              the PADDING box, so the third line paints into the 10px of bottom
              padding and is visible under the ellipsis (seen at 390px, round 5).
              Clamping a child with no padding of its own has no such edge, and
              is the form every engine we ship to honours - `overflow: clip` with
              `overflow-clip-margin: content-box` would fix the box directly but
              only Chromium takes the keyword. */}
          <Box component="span" className="hl-lead-clamp">
            {lead.txt}
          </Box>
        </Box>

        <Box
          className="hl-lead-score"
          sx={{
            mt: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "12.5px",
            color: "var(--hl-ink-3)",
          }}
        >
          <span>{scoreLabel}</span>
          <Box
            component="b"
            ref={scoreRef}
            sx={{
              fontFamily: "var(--hl-mono)",
              fontSize: "16px",
              color: accent,
              direction: "ltr",
            }}
          >
            {/* ONE string, not `{score}%`: two adjacent text nodes cannot be
                hydrated against the snapshot's single merged text node. */}
            {`${score}%`}
          </Box>
        </Box>
        <Box
          className="hl-lead-bar"
          aria-hidden="true"
          sx={{
            height: "5px",
            mt: "8px",
            borderRadius: "4px",
            background: "rgba(0,90,82,.08)",
            overflow: "hidden",
          }}
        >
          <Box
            ref={barRef}
            // The width is an inline style, not sx: the cycle writes it per
            // count through `barRef` (see HeroLive.writeScore), and an inline
            // style is what a direct `style.width` write overrides. The
            // prototype sets the bar to the FINAL score as the count starts
            // and lets the 1.4s ease carry it there while the number counts
            // up over 1.2s - so the bar leads the number, on purpose.
            style={{ width: `${score > 0 ? lead.score : 0}%` }}
            sx={{
              display: "block",
              height: "100%",
              borderRadius: "4px",
              transition: "width 1.4s var(--hl-ease)",
              // The HOT bar's angle is FLIPPED from the prototype's 270deg to
              // 90deg (owner round 4: "change the red -> gold gradient
              // direction right <-> left"). 270deg progresses leftward, so it
              // put --hl-ember-2 (#bf5867, the red) at the bar's left end and
              // --hl-ember-deep (#f4bfb0, the pale gold) at its right; 90deg
              // trades them. The colour STOPS are untouched - only the
              // direction - so nothing about the palette moves.
              //
              // The non-hot bar deliberately KEEPS 270deg: the owner asked
              // about the red -> gold bar only, and the teal pair is a design
              // he has already signed off. Flipping it too is one word here if
              // he wants the two states to sweep the same way.
              background: hot
                ? "linear-gradient(90deg, var(--hl-ember-deep), var(--hl-ember-2))"
                : "linear-gradient(270deg, var(--hl-teal), var(--hl-mint))",
              boxShadow: hot
                ? "0 0 14px rgba(191,88,103,.7)"
                : "0 0 12px rgba(127,212,193,.6)",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
});

export default React.memo(LeadCardLive);
